import * as actionTypes from './actionTypes';
import { UserActionTypes } from './actionTypes';
import { HttpError } from '../../config/Axios/axios-instance';
import { User } from '../../domain/User';
import { ListResults } from '../../common/List/List';

export type UserStateType = {
  user: User | null;
  userLoading: boolean;
  userError: HttpError;
  usersList: ListResults<User> | null;
  usersLoading: boolean;
  usersError: HttpError;
  userCreateLoading: boolean;
  userCreateError: HttpError;
  userCreateSuccess: boolean;
  userUpdateLoading: boolean;
  userUpdateError: HttpError;
  userUpdateSuccess: boolean;
  userDeleteLoading: boolean;
  userDeleteError: HttpError;
  userDeleteSuccess: boolean;
  usersListUpdateNeeded: boolean;
  currentUser: User | null;
  currentUserLoading: boolean;
  allUsersError: HttpError;
  allUsersList: ListResults<User> | null;
  allUsersLoading: boolean;
  updateUserInfoLoading: boolean;
  updateUserInfoError: HttpError;
  updateUserInfoSuccess: boolean;
  updateUserPasswordLoading: boolean;
  updateUserPasswordError: HttpError;
  updateUserPasswordSuccess: boolean;
  updateUserPasswordConfirmationLoading: boolean;
  updateUserPasswordConfirmationError: HttpError;
  updateUserPasswordConfirmationSuccess: boolean;
  createdUser: User | null;
};

export type UserActionType = UserStateType & {
  type: UserActionTypes;
};

export const initialState: UserStateType = {
  user: null,
  userLoading: false,
  userError: null,
  userCreateError: null,
  userCreateLoading: false,
  userCreateSuccess: false,
  userDeleteError: null,
  userDeleteLoading: false,
  userDeleteSuccess: false,
  userUpdateError: null,
  userUpdateLoading: false,
  userUpdateSuccess: false,
  usersError: null,
  usersList: null,
  usersLoading: true,
  usersListUpdateNeeded: false,
  currentUser: null,
  currentUserLoading: false,
  allUsersError: null,
  allUsersList: null,
  allUsersLoading: true,
  updateUserInfoError: null,
  updateUserInfoLoading: false,
  updateUserInfoSuccess: false,
  updateUserPasswordError: null,
  updateUserPasswordLoading: false,
  updateUserPasswordSuccess: false,
  updateUserPasswordConfirmationError: null,
  updateUserPasswordConfirmationLoading: false,
  updateUserPasswordConfirmationSuccess: false,
  createdUser: null,
};

const fetchCurrentUserStart = (state: UserStateType): UserStateType => ({
  ...state,
  currentUserLoading: true,
});

const fetchCurrentUserSuccess = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  currentUser: action.currentUser,
  currentUserLoading: false,
});

const fetchCurrentUserFail = (state: UserStateType): UserStateType => ({
  ...state,
  currentUserLoading: false,
});

const fetchUserStart = (state: UserStateType): UserStateType => ({
  ...state,
  userLoading: true,
});

const fetchUserSuccess = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  user: action.user,
  userLoading: false,
  userError: null,
});

const fetchUserFail = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  userError: action.userError,
  userLoading: false,
});

const fetchUsersStart = (state: UserStateType): UserStateType => ({
  ...state,
  usersLoading: true,
});

const fetchUsersSuccess = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  usersList: action.usersList,
  usersLoading: false,
  usersError: null,
  usersListUpdateNeeded: false,
});

const fetchUsersFail = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  usersError: action.usersError,
  usersLoading: false,
});

const fetchAllUsersStart = (state: UserStateType): UserStateType => ({
  ...state,
  allUsersLoading: true,
});

const fetchAllUsersSuccess = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  allUsersList: action.allUsersList,
  allUsersLoading: false,
  allUsersError: null,
});

const fetchAllUsersFail = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  allUsersError: action.allUsersError,
  allUsersLoading: false,
});

const createUserStart = (state: UserStateType): UserStateType => ({
  ...state,
  userCreateLoading: true,
});

const createUserSuccess = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  userCreateLoading: false,
  userCreateError: null,
  userCreateSuccess: true,
  usersListUpdateNeeded: true,
  createdUser: action.createdUser,
});

const createUserFail = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  userCreateLoading: false,
  userCreateError: action.userCreateError,
});

const updateUserStart = (state: UserStateType): UserStateType => ({
  ...state,
  userUpdateLoading: true,
});

const updateUserSuccess = (state: UserStateType): UserStateType => ({
  ...state,
  userUpdateLoading: false,
  userUpdateError: null,
  userUpdateSuccess: true,
  usersListUpdateNeeded: true,
});

const updateUserFail = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  userUpdateLoading: false,
  userUpdateError: action.userUpdateError,
});

const deleteUserStart = (state: UserStateType): UserStateType => ({
  ...state,
  userDeleteLoading: true,
});

const deleteUserSuccess = (state: UserStateType): UserStateType => ({
  ...state,
  userDeleteLoading: false,
  userDeleteError: null,
  userDeleteSuccess: true,
  usersListUpdateNeeded: true,
});

const deleteUserFail = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  userDeleteLoading: false,
  userDeleteError: action.userDeleteError,
});

const updateUserInfoStart = (state: UserStateType): UserStateType => ({
  ...state,
  updateUserInfoLoading: true,
});

const updateUserInfoSuccess = (state: UserStateType): UserStateType => ({
  ...state,
  updateUserInfoLoading: false,
  updateUserInfoError: null,
  updateUserInfoSuccess: true,
});

const updateUserInfoFail = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  updateUserInfoLoading: false,
  updateUserInfoError: action.updateUserInfoError,
});

const resetUserInfoStore = (state: UserStateType): UserStateType => ({
  ...state,
  updateUserInfoError: null,
  updateUserInfoLoading: false,
  updateUserInfoSuccess: false,
});

const updateUserPasswordStart = (state: UserStateType): UserStateType => ({
  ...state,
  updateUserPasswordLoading: true,
});

const updateUserPasswordSuccess = (state: UserStateType): UserStateType => ({
  ...state,
  updateUserPasswordLoading: false,
  updateUserPasswordError: null,
  updateUserPasswordSuccess: true,
});

const updateUserPasswordFail = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  updateUserPasswordLoading: false,
  updateUserPasswordError: action.updateUserPasswordError,
});

const resetUserPasswordStore = (state: UserStateType): UserStateType => ({
  ...state,
  updateUserPasswordError: null,
  updateUserPasswordLoading: false,
  updateUserPasswordSuccess: false,
});

const updateUserPasswordConfirmationStart = (
  state: UserStateType,
): UserStateType => ({
  ...state,
  updateUserPasswordConfirmationLoading: true,
});

const updateUserPasswordConfirmationSuccess = (
  state: UserStateType,
): UserStateType => ({
  ...state,
  updateUserPasswordConfirmationLoading: false,
  updateUserPasswordConfirmationError: null,
  updateUserPasswordConfirmationSuccess: true,
});

const updateUserPasswordConfirmationFail = (
  state: UserStateType,
  action: UserActionType,
): UserStateType => ({
  ...state,
  updateUserPasswordConfirmationLoading: false,
  updateUserPasswordConfirmationError:
    action.updateUserPasswordConfirmationError,
});

const resetUserListStore = (state: UserStateType): UserStateType => ({
  ...state,
  usersList: null,
  usersLoading: false,
  usersError: null,
  usersListUpdateNeeded: false,
});

const resetUserStore = (state: UserStateType): UserStateType => ({
  ...initialState,
  currentUser: state.currentUser,
});

const completelyResetUserStore = (): UserStateType => ({
  ...initialState,
});

const resetCreateUserStore = (state: UserStateType): UserStateType => ({
  ...state,
  createdUser: null,
});

const reducer = (state = initialState, action: UserActionType) => {
  switch (action.type) {
    case actionTypes.FETCH_USER_START:
      return fetchUserStart(state);
    case actionTypes.FETCH_USER_SUCCESS:
      return fetchUserSuccess(state, action);
    case actionTypes.FETCH_USER_FAIL:
      return fetchUserFail(state, action);
    case actionTypes.FETCH_USERS_START:
      return fetchUsersStart(state);
    case actionTypes.FETCH_USERS_SUCCESS:
      return fetchUsersSuccess(state, action);
    case actionTypes.FETCH_USERS_FAIL:
      return fetchUsersFail(state, action);
    case actionTypes.FETCH_ALL_USERS_START:
      return fetchAllUsersStart(state);
    case actionTypes.FETCH_ALL_USERS_SUCCESS:
      return fetchAllUsersSuccess(state, action);
    case actionTypes.FETCH_ALL_USERS_FAIL:
      return fetchAllUsersFail(state, action);
    case actionTypes.CREATE_USER_START:
      return createUserStart(state);
    case actionTypes.CREATE_USER_SUCCESS:
      return createUserSuccess(state, action);
    case actionTypes.CREATE_USER_FAIL:
      return createUserFail(state, action);
    case actionTypes.UPDATE_USER_START:
      return updateUserStart(state);
    case actionTypes.UPDATE_USER_SUCCESS:
      return updateUserSuccess(state);
    case actionTypes.UPDATE_USER_FAIL:
      return updateUserFail(state, action);
    case actionTypes.DELETE_USER_START:
      return deleteUserStart(state);
    case actionTypes.DELETE_USER_SUCCESS:
      return deleteUserSuccess(state);
    case actionTypes.DELETE_USER_FAIL:
      return deleteUserFail(state, action);
    case actionTypes.FETCH_ME_START:
      return fetchCurrentUserStart(state);
    case actionTypes.FETCH_ME_SUCCESS:
      return fetchCurrentUserSuccess(state, action);
    case actionTypes.FETCH_ME_FAIL:
      return fetchCurrentUserFail(state);
    case actionTypes.UPDATE_USER_INFO_START:
      return updateUserInfoStart(state);
    case actionTypes.UPDATE_USER_INFO_SUCCESS:
      return updateUserInfoSuccess(state);
    case actionTypes.UPDATE_USER_INFO_FAIL:
      return updateUserInfoFail(state, action);
    case actionTypes.RESET_USER_INFO_STORE:
      return resetUserInfoStore(state);
    case actionTypes.UPDATE_USER_PASSWORD_START:
      return updateUserPasswordStart(state);
    case actionTypes.UPDATE_USER_PASSWORD_SUCCESS:
      return updateUserPasswordSuccess(state);
    case actionTypes.UPDATE_USER_PASSWORD_FAIL:
      return updateUserPasswordFail(state, action);
    case actionTypes.RESET_USER_PASSWORD_STORE:
      return resetUserPasswordStore(state);
    case actionTypes.UPDATE_USER_PASSWORD_CONFIRMATION_START:
      return updateUserPasswordConfirmationStart(state);
    case actionTypes.UPDATE_USER_PASSWORD_CONFIRMATION_SUCCESS:
      return updateUserPasswordConfirmationSuccess(state);
    case actionTypes.UPDATE_USER_PASSWORD_CONFIRMATION_FAIL:
      return updateUserPasswordConfirmationFail(state, action);
    case actionTypes.RESET_USER_LIST_STORE:
      return resetUserListStore(state);
    case actionTypes.RESET_USER_STORE:
      return resetUserStore(state);
    case actionTypes.RESET_CREATED_USER:
      return resetCreateUserStore(state);
    case actionTypes.LOGOUT:
      return completelyResetUserStore();
    default:
      return state;
  }
};

export default reducer;
